import React from 'react';
import { Alert, Box, CircularProgress } from '@mui/material';

interface EmbeddedDetailTabProps<T> {
  entityId: string;
  initialData?: T;
  load: (id: string) => Promise<T>;
  children: (data: T) => React.ReactNode;
}

/** Standard loading/error/layout shell for entity forms embedded in another module's tab. */
export default function EmbeddedDetailTab<T>({
  entityId,
  initialData,
  load,
  children,
}: EmbeddedDetailTabProps<T>) {
  const [data, setData] = React.useState<T | undefined>(initialData);
  const [loading, setLoading] = React.useState(!initialData);
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    void load(entityId)
      .then((detail) => {
        if (active) setData(detail);
      })
      .catch((requestError: any) => {
        if (active) setError(requestError?.response?.data?.detail || requestError?.message || 'Không thể tải dữ liệu chi tiết.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [entityId, load]);

  if (loading && !data) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={28} /></Box>;
  }
  if (!data) {
    return <Alert severity='error'>{error || 'Không tìm thấy dữ liệu.'}</Alert>;
  }

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', minWidth: 0, boxSizing: 'border-box', overflowX: 'hidden' }}>
      {error && <Alert severity='error' sx={{ mb: 2 }}>{error}</Alert>}
      {children(data)}
    </Box>
  );
}
